import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { CalendarClock, ClipboardList, Info, Loader2, UserRound } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@bookdental/ui";
import { trpc } from "@/lib/trpc";
import { AJUDA_PACIENTES } from "@/lib/ajuda/dialogos/pacientes";
import { STATUS_META, formatDate, orderServiceSummary } from "@/components/prosthesis/prosthesis-meta";

export type PatientSummaryTarget = {
  patientId: string;
  organizationId: string;
  patientName: string;
  /** Nome da clínica de onde veio o compromisso — sem isso, na conta Pessoal o aviso mandava
   *  "troque pra organização desse paciente" sem dizer qual. */
  organizationName?: string | null;
};

type Props = {
  target: PatientSummaryTarget | null;
  onClose: () => void;
};

/** Resumo só-leitura de um paciente (dados básicos + próxima consulta + pedidos em aberto) —
 *  aberto pelo ícone de "ver resumo" nas visões agregadas da organização Pessoal. Não navega pra
 *  ficha nem permite editar — pra agir, o dono precisa trocar pra organização daquele paciente. */
export function PatientSummaryDialog({ target, onClose }: Props) {
  const open = !!target;
  const query = trpc.patient.getSummaryForOwner.useQuery(
    target ? { patientId: target.patientId, organizationId: target.organizationId } : ({} as any),
    { enabled: !!target },
  );

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md dark:border-border dark:bg-card">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 dark:text-foreground" ajuda={AJUDA_PACIENTES.resumoDoPaciente}>
            <UserRound className="h-4 w-4 text-brand-600 dark:text-primary" />
            {target?.patientName}
          </DialogTitle>
        </DialogHeader>

        {query.isLoading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-brand-600" />
          </div>
        ) : query.isError ? (
          <p className="py-6 text-center text-sm text-zinc-500 dark:text-muted-foreground">
            {query.error?.message || "Não foi possível carregar o resumo."}
          </p>
        ) : (
          <div className="space-y-4">
            <div className="flex items-start gap-2.5 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300">
              <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <p className="text-[12.5px]">
                {target?.organizationName ? (
                  <>
                    Consulta de <strong className="font-semibold">{target.organizationName}</strong>. Só leitura
                    aqui — pra agendar, editar ou pedir prótese, troque pra essa organização.
                  </>
                ) : (
                  "Só leitura. Pra agendar, editar ou pedir prótese, troque pra organização desse paciente."
                )}
              </p>
            </div>

            <div>
              <p className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-zinc-500 dark:text-muted-foreground">
                <CalendarClock className="h-3.5 w-3.5" />
                Próxima consulta
              </p>
              {query.data?.nextAppointment ? (
                <div className="rounded-lg border border-zinc-200 bg-zinc-50/60 px-3 py-2 text-sm dark:border-border dark:bg-muted/30">
                  <p className="font-medium text-zinc-900 dark:text-foreground">
                    {format(new Date(query.data.nextAppointment.date), "dd 'de' MMMM", { locale: ptBR })} ·{" "}
                    {query.data.nextAppointment.startTime}–{query.data.nextAppointment.endTime}
                  </p>
                  <p className="text-xs text-zinc-500 dark:text-muted-foreground">
                    {query.data.nextAppointment.procedure}
                    {query.data.nextAppointment.dentistName ? ` · ${query.data.nextAppointment.dentistName}` : ""}
                  </p>
                </div>
              ) : (
                <p className="text-sm text-zinc-500 dark:text-muted-foreground">Nenhuma consulta agendada.</p>
              )}
            </div>

            <div>
              <p className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-zinc-500 dark:text-muted-foreground">
                <ClipboardList className="h-3.5 w-3.5" />
                Pedidos em aberto
              </p>
              {query.data?.openOrders && query.data.openOrders.length > 0 ? (
                <ul className="space-y-1.5">
                  {query.data.openOrders.map((o) => (
                    <li
                      key={o.id}
                      className="flex items-center justify-between gap-2 rounded-lg border border-zinc-200 bg-zinc-50/60 px-3 py-2 text-sm dark:border-border dark:bg-muted/30"
                    >
                      <div className="min-w-0">
                        <p className="truncate font-medium text-zinc-900 dark:text-foreground">
                          {o.labName || "—"}
                        </p>
                        <p className="truncate text-xs text-zinc-600 dark:text-muted-foreground">
                          {orderServiceSummary(o.services)}
                        </p>
                        <p className="text-xs text-zinc-500 dark:text-muted-foreground">
                          Prazo: {formatDate(o.deadline)}
                        </p>
                      </div>
                      <span
                        className="shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold"
                        style={{ backgroundColor: STATUS_META[o.status].bg, color: STATUS_META[o.status].color }}
                      >
                        {STATUS_META[o.status].label}
                      </span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-zinc-500 dark:text-muted-foreground">Nenhum pedido em aberto.</p>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
